import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTutorial } from "../tutorial/TutorialContext";
import { useVersionSwitch } from "./useVersionSwitch";

// The top nav's bell. It carries exactly one notification today — that the user
// is on the new UI, with the way into the tour and the way back to the previous
// version. That message used to be a 28px strip pinned above everything; it
// lives here now so the chrome is one bar instead of two.
//
// GUNDI-5665: the way back to legacy must stay reachable and must not be
// dismissible. So the notification is never removed — opening the panel only
// clears the unread dot. A permanent unread badge is the thing people learn to
// ignore, and a dismissible one would take the switch away with it.
const SEEN_KEY = "gundiNewUiNotificationSeen";

// localStorage throws in some privacy modes, and a bell that crashes the nav is
// worse than one that always looks unread.
const readSeen = () => {
    try {
        return localStorage.getItem(SEEN_KEY) === "true";
    } catch {
        return false;
    }
};

const BellIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="24" viewBox="0 0 20 24" fill="none" aria-hidden="true">
        <path d="M9.84615 24C11.2 24 12.3077 22.8923 12.3077 21.5385H7.38462C7.38462 22.8923 8.48 24 9.84615 24ZM17.2308 16.6154V10.4615C17.2308 6.68308 15.2123 3.52 11.6923 2.68308V1.84615C11.6923 0.824615 10.8677 0 9.84615 0C8.82462 0 8 0.824615 8 1.84615V2.68308C4.46769 3.52 2.46154 6.67077 2.46154 10.4615V16.6154L0 19.0769V20.3077H19.6923V19.0769L17.2308 16.6154Z" fill="currentColor" />
    </svg>
);

const NotificationsMenu = () => {
    const { t } = useTranslation();
    const { requestStart } = useTutorial();
    const { switchTo, switching } = useVersionSwitch();
    const [open, setOpen] = useState(false);
    const [seen, setSeen] = useState(readSeen);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const openPanel = () => {
        setOpen((prev) => !prev);
        if (!seen) {
            setSeen(true);
            try {
                localStorage.setItem(SEEN_KEY, "true");
            } catch {
                // A viewer who cannot persist this just keeps seeing the dot.
            }
        }
    };

    useEffect(() => {
        const onClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setOpen(false);
        };
        const onEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        document.addEventListener("keydown", onEscape);
        return () => {
            document.removeEventListener("mousedown", onClickOutside);
            document.removeEventListener("keydown", onEscape);
        };
    }, []);

    return (
        <div ref={wrapperRef} className="relative flex items-center">
            <button
                type="button"
                data-testid="NotificationsMenu-trigger"
                aria-label={t("Notifications")}
                aria-expanded={open}
                onClick={openPanel}
                className="relative flex items-center text-ui-text-secondary-text hover:opacity-80"
            >
                <BellIcon />
                {!seen && (
                    <span
                        data-testid="NotificationsMenu-unread-dot"
                        className="absolute -top-0.5 -right-1 w-2.5 h-2.5 rounded-full bg-ui-status-error-destructive border border-white"
                    />
                )}
            </button>

            <div
                className={!open ? "hidden" : "z-30 absolute top-full right-0 mt-2 w-[320px] bg-white shadow-dropdown rounded overflow-hidden"}
                data-testid="NotificationsMenu-panel"
            >
                <div className="px-4 py-2.5 border-b border-unlisted-gray-lines text-sm font-semibold text-ui-text-primary-text">
                    {t("Notifications")}
                </div>
                <div className="px-4 py-3">
                    <p className="text-sm text-ui-text-primary-text">{t("You're viewing the new version of Gundi.")}</p>
                    <div className="flex flex-col items-start gap-2 mt-2">
                        <button
                            type="button"
                            data-testid="NotificationsMenu-tutorial-button"
                            onClick={() => { setOpen(false); requestStart(); }}
                            // Purple on purpose (product call, carried over from
                            // the banner): the tour should stand out from the
                            // grey version-switch link beside it.
                            className="text-xs font-semibold text-unlisted-purple hover:text-unlisted-dark-purple"
                        >
                            {t("Take a tour")}
                        </button>
                        <button
                            type="button"
                            data-testid="NotificationsMenu-switch-button"
                            disabled={switching}
                            onClick={() => switchTo("legacy")}
                            className="text-xs font-semibold disabled:opacity-50 text-ui-text-secondary-text hover:text-ui-text-primary-text"
                        >
                            {t("Switch back to the previous version")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationsMenu;
